/**
 * OverView trend-meter colors — single global source of truth (Round 92).
 *
 * The Charts "OverView" dashboard and the "Bkground" tint both read their
 * colors from here so the whole feature can be re-skinned in one place.
 * Chart Settings → OverView can override any of these per-user; these are the
 * defaults that seed that settings section.
 *
 * Greens/oranges/reds at these saturations read well on BOTH light and dark
 * chart backgrounds; the background tint uses a low alpha so it never fights
 * the candles in either theme.
 */

export type TrendDir = 'up' | 'down' | 'neutral' | 'na';

export type OverviewZone =
  | 'strongShort'
  | 'probableShort'
  | 'neutral'
  | 'probableBuy'
  | 'strongBuy';

/** Zone fill colors for the speedometer arc + zone label. */
export const OVERVIEW_ZONE_COLORS: Record<OverviewZone, string> = {
  strongShort:   '#e53935', // red
  probableShort: '#ffa726', // orange
  neutral:       '#9e9e9e', // grey
  probableBuy:   '#66bb6a', // light green
  strongBuy:     '#00c853', // bright green
};

/** Per-indicator trend shape colors. */
export const OVERVIEW_SHAPE_COLORS: Record<'up' | 'down' | 'neutral', string> = {
  up:      '#26a69a', // teal-green
  down:    '#ef5350', // red
  neutral: '#fbc02d', // amber
};

export const ZONE_LABELS: Record<OverviewZone, string> = {
  strongShort:   'Strong Short',
  probableShort: 'Probable Short',
  neutral:       'Neutral',
  probableBuy:   'Probable Buy',
  strongBuy:     'Strong Buy',
};

/** Default score-zone thresholds (|score| boundaries). */
export const OVERVIEW_DEFAULT_THRESHOLDS = { strong: 60, probable: 20 };

/** Map a -100..+100 score to its zone. */
export function zoneForScore(
  score: number,
  strong = OVERVIEW_DEFAULT_THRESHOLDS.strong,
  probable = OVERVIEW_DEFAULT_THRESHOLDS.probable,
): OverviewZone {
  if (score >= strong) return 'strongBuy';
  if (score >= probable) return 'probableBuy';
  if (score > -probable) return 'neutral';
  if (score > -strong) return 'probableShort';
  return 'strongShort';
}

/** Parse #rrggbb / #rgb → [r,g,b] or null. */
function parseHex(hex: string): [number, number, number] | null {
  let h = (hex || '').replace('#', '').trim();
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some(n => Number.isNaN(n))) return null;
  return [r, g, b];
}

/** Blend `color` into `base` by `ratio` (0..1) → a solid #rrggbb. Used for the
 *  Bkground tint so the result is a flat canvas color that reads on light/dark. */
export function mixHex(base: string, color: string, ratio: number): string {
  const a = parseHex(base), b = parseHex(color);
  if (!a || !b) return base;
  const t = Math.max(0, Math.min(1, ratio));
  const mix = (i: number) => Math.round(a[i] * (1 - t) + b[i] * t);
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(mix(0))}${toHex(mix(1))}${toHex(mix(2))}`;
}

/** #rrggbb (or #rgb) → rgba(r,g,b,a). Falls back to the input on a parse miss. */
export function hexToRgba(hex: string, alpha: number): string {
  let h = (hex || '').replace('#', '').trim();
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some(n => Number.isNaN(n))) return hex;
  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, alpha))})`;
}
